/**
 * Générateur — Résoudre des problèmes mobilisant addition et soustraction de
 * nombres décimaux relatifs.
 * Attendu ID : 922 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 5)
 *
 * Plan de cartes :
 *   « dates » (Ex.10) : durées et dates avec années négatives — 4 variantes en
 *     rotation (durée de vie, durée d'un empire, année de mort — B2, année de
 *     naissance — B2). Saisie + QCM (le distracteur clé : b - a au lieu de
 *     b + a pour une durée qui traverse l'an 0).
 *   « variation » (Ex.11) : la soustraction comme différence entre deux ÉTATS
 *     (cœur du cadrage) — saisie + QCM, contextes variés.
 *   « bilan » (Ex.9bis) : bilan d'une somme de variations signées — saisie.
 *   « ecart » (Ex.10bis) : application de variations successives à un état —
 *     qcm_unique (choisir l'EXPRESSION correcte) + texte (résultat final).
 *
 * Durées toujours positives (saisie nue) ; années/écarts signés imposés.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 922;
const L_DATES = 'Durées et dates (années négatives)';
const L_VARIATION = 'La variation entre deux états';
const L_BILAN = 'Le bilan d\'une journée';
const L_ECART = 'Variations successives d\'un écart';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const fr = (v) => String(v);
const signe = (v) => (v === 0 ? '0' : v < 0 ? String(v) : `+${v}`);

const PERSONNAGES = ['Césarius', 'Vitrius', 'Planus', 'Alexius', 'Livia', 'Marcus'];

// ═════════ Carte 1 — durées et dates (Ex.10) ═════════

const tirerDate = () => {
    const nom = randomChoice(PERSONNAGES);
    const type = randomChoice(['vecu', 'empire', 'mort_en', 'ne_en']);
    if (type === 'vecu') {
        const a = randInt(10, 90);   // né en -a
        const b = randInt(5, 80);    // mort en b
        return { type, texte: `${nom}, né en l'an -${a}, est mort en l'an ${b}.`, question: 'Combien d\'années a-t-il vécu ?', reponse: String(a + b), suffixe: 'ans' };
    }
    if (type === 'empire') {
        const a = randInt(100, 600) ;
        const b = randInt(50, 400);
        return { type, texte: `L'Empire de ${nom} a été créé en l'an -${a} et s'est terminé en l'an ${b}.`, question: 'Combien d\'années a-t-il duré ?', reponse: String(a + b), suffixe: 'ans' };
    }
    if (type === 'mort_en') {
        const a = randInt(10, 80);   // né en -a
        const n = randInt(a + 5, a + 75); // mort APRÈS l'an 0 (année positive, écriture nue)
        return { type, texte: `${nom} est né en l'an -${a} et est mort à ${n} ans.`, question: 'En quelle année est-il mort ?', reponse: String(n - a), suffixe: '' };
    }
    const b = randInt(50, 300);      // mort en -b
    const n = randInt(20, 80);       // à n ans → né en -(b+n)
    return { type, texte: `${nom} est mort à ${n} ans, en l'an -${b}.`, question: 'En quelle année est-il né ? (année négative : écris-la avec le signe -)', reponse: String(-(b + n)), suffixe: '' };
};

const genDatesSaisie = () => {
    const d = tirerDate();
    const payload = makeSaisie(d.reponse,
        `<p>${d.texte} <b>${d.question}</b></p>`,
        { ...opts(L_DATES), suffixe: d.suffixe });
    payload._meta.params = { type: d.type, reponse: d.reponse };
    return payload;
};

const genDatesQcm = () => {
    const d = tirerDate();
    const r = parseInt(d.reponse, 10);
    const cands = [String(-r), String(Math.abs(r) - 2 * Math.min(Math.abs(r) - 1, 40)), String(r + 10), String(r - 10)];
    const distracteurs = [];
    cands.forEach(c => {
        if (distracteurs.length < 3 && c !== d.reponse && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([d.reponse, ...distracteurs], d.reponse,
        `<p>${d.texte} <b>${d.question}</b>${d.suffixe ? ` (en ${d.suffixe})` : ''}</p>`,
        opts(L_DATES));
    payload._meta.params = { type: d.type, reponse: d.reponse };
    return payload;
};

// ═════════ Carte 2 — variation entre deux états (Ex.11) ═════════

const CONTEXTES_VAR = [
    { unite: 'kg', txt: (e1, e2, s1, s2) => `On mesure la différence entre déchets produits et recyclés. Semaine ${s1} : ${signe(e1)} kg ; semaine ${s2} : ${signe(e2)} kg.` },
    { unite: '°C', txt: (e1, e2, s1, s2) => `On relève la température. Jour ${s1} : ${signe(e1)} °C ; jour ${s2} : ${signe(e2)} °C.` },
    { unite: 'cm', txt: (e1, e2, s1, s2) => `On mesure le niveau d'un bassin par rapport au repère. Mois ${s1} : ${signe(e1)} cm ; mois ${s2} : ${signe(e2)} cm.` },
    { unite: '€', txt: (e1, e2, s1, s2) => `On note le solde d'une cagnotte. Semaine ${s1} : ${signe(e1)} € ; semaine ${s2} : ${signe(e2)} €.` },
];

const tirerVariation = () => {
    const ctx = randomChoice(CONTEXTES_VAR);
    const e1 = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 15);
    let e2 = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 15);
    if (e2 === e1) e2 = e1 + randInt(1, 5);
    const s1 = randInt(1, 3);
    return { ctx, e1, e2, s1, s2: s1 + randInt(1, 3), variation: e2 - e1 };
};

const genVariationSaisie = () => {
    const v = tirerVariation();
    const phrase = `Variation entre les deux relevés : {{${signe(v.variation)}}} ${v.ctx.unite}`;
    const payload = makeTexteTrou(phrase,
        `<p>${v.ctx.txt(v.e1, v.e2, v.s1, v.s2)} <b>Calcule la variation entre le relevé ${v.s1} et le relevé ${v.s2}.</b> `
        + 'Écris-la avec son signe (ex. : -5 ou +8).</p>',
        { ...opts(L_VARIATION), mode: 'saisie' });
    payload._meta.params = { e1: v.e1, e2: v.e2 };
    return payload;
};

const genVariationQcm = () => {
    const v = tirerVariation();
    const correct = signe(v.variation);
    const cands = [signe(-v.variation), signe(v.e1 + v.e2), signe(v.variation + 2), signe(v.variation - 2)];
    const distracteurs = [];
    cands.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>${v.ctx.txt(v.e1, v.e2, v.s1, v.s2)} <b>Quelle est la variation (en ${v.ctx.unite}) entre le relevé ${v.s1} et le relevé ${v.s2} ?</b></p>`,
        opts(L_VARIATION));
    payload._meta.params = { e1: v.e1, e2: v.e2 };
    return payload;
};

// ═════════ Carte 3 — bilan de variations (Ex.9bis) ═════════

const CONTEXTES_BILAN = [
    {
        unite: 'kg de CO₂',
        intro: 'Une commune étudie son bilan carbone journalier (émissions positives, économies négatives).',
        postes: ['les transports', 'une coupure d\'éclairage', 'des livraisons', 'la plantation d\'arbres'],
    },
    {
        unite: 'points',
        intro: 'Un club compte ses points de la semaine (gains positifs, pénalités négatives).',
        postes: ['le match de lundi', 'une pénalité mardi', 'le match de jeudi', 'un forfait samedi'],
    },
    {
        unite: '€',
        intro: 'On suit le compte d\'une association (recettes positives, dépenses négatives).',
        postes: ['les adhésions', 'l\'achat de matériel', 'une subvention', 'la location de la salle'],
    },
];

const genBilanSaisie = () => {
    const ctx = randomChoice(CONTEXTES_BILAN);
    const signes = shuffle([1, 1, -1, -1]);
    const valeurs = signes.map(s => s * randInt(8, 60));
    const detail = ctx.postes.map((p, i) => `${p} : ${signe(valeurs[i])} ${ctx.unite}`).join(' ; ');
    const total = valeurs.reduce((s, v) => s + v, 0);
    const phrase = `Bilan total : {{${signe(total)}}} ${ctx.unite}`;
    const payload = makeTexteTrou(phrase,
        `<p>${ctx.intro} ${detail}. <b>Calcule le bilan total.</b> Écris-le avec son signe, ou 0 s'il est nul.</p>`,
        { ...opts(L_BILAN), mode: 'saisie' });
    payload._meta.params = { valeurs };
    return payload;
};

// ═════════ Carte 4 — variations successives d'un écart (Ex.10bis) ═════════

const tirerEcart = () => {
    const e0 = randInt(5, 20);
    const variations = [1, 2, 3, 4].map(() => (randomChoice([true, false]) ? 1 : -1) * randInt(2, 9));
    return { e0, variations, final: e0 + variations.reduce((s, v) => s + v, 0) };
};

const MOIS = ['en mars', 'en juin', 'en septembre', 'en décembre'];

const texteEcart = (t) =>
    `Dans une entreprise, on suit l'écart d'effectifs entre hommes et femmes. En janvier, l'écart est de ${signe(t.e0)}. `
    + t.variations.map((v, i) => `${MOIS[i].charAt(0).toUpperCase() + MOIS[i].slice(1)}, il varie de ${signe(v)}.`).join(' ');

const genEcartExpressionQcm = () => {
    const t = tirerEcart();
    const correct = `${t.e0} + ${t.variations.map(v => `(${signe(v)})`).join(' + ')}`;
    const distracteurs = [
        `${t.e0} + ${t.variations.map(v => Math.abs(v)).join(' + ')}`,                       // signes oubliés
        `${t.e0} - ${t.variations.map(v => `(${signe(v)})`).join(' - ')}`,                   // soustractions à tort
        t.variations.map(v => `(${signe(v)})`).join(' + '),                                  // état initial oublié
    ];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>${texteEcart(t)} <b>Quel calcul donne l'écart en fin d'année ?</b></p>`,
        opts(L_ECART));
    payload._meta.params = { e0: t.e0, variations: t.variations };
    return payload;
};

const genEcartResultat = () => {
    const t = tirerEcart();
    const payload = makeSaisie(signe(t.final),
        `<p>${texteEcart(t)} <b>Quel est l'écart en fin d'année ?</b> Écris-le avec son signe (ex. : +8 ou -3), ou 0 s'il est nul.</p>`,
        opts(L_ECART));
    payload._meta.params = { e0: t.e0, variations: t.variations, final: t.final };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    dates_texte: genDatesSaisie,
    dates_qcm_unique: genDatesQcm,
    variation_texte_trou_saisie: genVariationSaisie,
    variation_qcm_unique: genVariationQcm,
    bilan_texte_trou_saisie: genBilanSaisie,
    ecart_expression_qcm_unique: genEcartExpressionQcm,
    ecart_resultat_texte: genEcartResultat,
};

const FORMAT_LABELS = {
    texte: 'Saisie', qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous',
    expression_qcm_unique: 'QCM Expression', resultat_texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'dates', label: L_DATES, formatsList: buildFormats('dates') },
    { id: 'variation', label: L_VARIATION, formatsList: buildFormats('variation') },
    { id: 'bilan', label: L_BILAN, formatsList: buildFormats('bilan') },
    { id: 'ecart', label: L_ECART, formatsList: buildFormats('ecart') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
